import { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FaGithub } from 'react-icons/fa';
import { BsBoxArrowUpRight } from 'react-icons/bs';
import { useTheme, DARK, LIGHT } from '../context/ThemeContext';

// ── Import your project screenshots ──────────────────────────
import taskListImg from '../assets/img/Task_List.png';
import firebugLogin from '../assets/img/firebug_login.jpeg';
import hrmsImg from '../assets/img/hrms_dashboard.png';
// Add more imports as you add more projects

const projects = [
  // ── REAL PROJECT 1 ───────────────────────────────────────
  {
    title: 'FireBug — Equipment Tracking System',
    description:
      'An enterprise-grade healthcare equipment tracking and inventory management system built for General Data Company. Features a real-time dashboard showing overdue and due inspections, equipment management with tag/device/location filtering, route wizard, maintenance scheduling, inspection result analytics with charts, and role-based access control with secure login.',
    images: [firebugLogin],
    tags: ['Java', 'Angular', 'REST API', 'PostgreSQL', 'Healthcare'],
    github: null,   // private company project
    demo: null,
    featured: true,
    isPrivate: true,
    company: 'General Data Company',
  },

   // ── REAL PROJECT 2 ───────────────────────────────────────
   {
    title: 'HR Management System',
    description:
      'A full-stack enterprise Human Resources Management System built for iSGM, enabling a sustainable, paperless office environment. Features real-time attendance monitoring with monthly bar charts, payroll calculation, leave and overtime management, department-wise reporting with pie charts, announcement board, and role-based approval workflows for attendance, leave, and overtime requests.',
    images: [hrmsImg],
    tags: ['Java', 'React.js', 'Spring Security', 'REST API', 'PostgreSQL'],
    github: null,
    demo: 'https://www.isgm2.com/solutions/human-resources-management-system',
    featured: true,
    isPrivate: false,
    company: 'iSGM',
  },

  // ── REAL PROJECT 3 ───────────────────────────────────────
  {
    title: 'ToDo List Mini Web Application',
    description:
      'A full-stack task management application with user registration, JWT-secured login, and complete task lifecycle management. Supports priority levels (HIGH, MEDIUM, LOW) and status tracking (PENDING, IN PROGRESS, COMPLETED). Backend documented with Swagger UI.',
    images: [taskListImg],
    tags: ['Java', 'React.js', 'PostgreSQL', 'JWT', 'Swagger', 'Bootstrap'],
    github: 'https://github.com/SandhyaMIU/ToDo-List-Mini-Web',
    demo: null,
    featured: false,
    isPrivate: false,
    company: null,
  },


];

const Projects = () => {
  const { isDark } = useTheme();
  const t = isDark ? DARK : LIGHT;
  const [hovered, setHovered] = useState(null);
  const [activeImage, setActiveImage] = useState({});  // track active image per project

  const getActiveImage = (i, images) => {
    return images[activeImage[i] ?? 0];
  };

  return (
    <section id="projects" style={{ background: t.bg, padding: '60px 0', transition: 'background 0.3s' }}>
      <Container>

        {/* ── Header ── */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '3px', textTransform: 'uppercase', color: t.accent, marginBottom: '8px' }}>
            Work
          </div>
          <div style={{ fontSize: '26px', fontWeight: 700, color: t.text }}>Projects</div>
        </div>

        <Row>
          {projects.map((project, i) => {
            const isHovered = hovered === i;
            const hasImages = project.images && project.images.length > 0;
            const currentImg = hasImages ? getActiveImage(i, project.images) : null;

            return (
              <Col key={i} sm={12} md={6} lg={4} className="mb-4">
                <div
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    background: t.bgAlt,
                    border: `1px solid ${isHovered ? t.borderHover : t.border}`,
                    borderRadius: '14px',
                    overflow: 'hidden',
                    transition: 'all 0.25s ease',
                    boxShadow: isHovered ? t.shadow : 'none',
                    transform: isHovered ? 'translateY(-4px)' : 'none',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                  }}
                >
                  {/* ── Badges ── */}
                  <div style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 2, display: 'flex', gap: '6px' }}>
                    {project.featured && (
                      <span style={{
                        background: t.accent, color: '#fff',
                        fontSize: '10px', fontWeight: 700,
                        letterSpacing: '1px', padding: '3px 10px',
                        borderRadius: '20px', textTransform: 'uppercase',
                      }}>Featured</span>
                    )}
                    {project.isPrivate && (
                      <span style={{
                        background: '#8b949e33', color: t.textSub,
                        border: `1px solid ${t.border}`,
                        fontSize: '10px', fontWeight: 600,
                        letterSpacing: '0.5px', padding: '3px 10px',
                        borderRadius: '20px',
                      }}>🔒 Private</span>
                    )}
                  </div>

                  {/* ── Screenshot / Placeholder ── */}
                  {hasImages ? (
                    <div style={{ position: 'relative', overflow: 'hidden', height: '160px' }}>
                      <img
                        src={currentImg}
                        alt={project.title}
                        style={{
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.4s ease',
                          transform: isHovered ? 'scale(1.04)' : 'scale(1)',
                        }}
                      />
                      <div style={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 60%)',
                      }} />

                      {/* Image switcher dots — only if multiple images */}
                      {project.images.length > 1 && (
                        <div style={{
                          position: 'absolute', bottom: '10px', left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex', gap: '6px',
                        }}>
                          {project.images.map((_, imgIdx) => (
                            <button
                              key={imgIdx}
                              onClick={() => setActiveImage(prev => ({ ...prev, [i]: imgIdx }))}
                              style={{
                                width: (activeImage[i] ?? 0) === imgIdx ? '20px' : '8px',
                                height: '8px',
                                borderRadius: '10px',
                                border: 'none',
                                background: (activeImage[i] ?? 0) === imgIdx ? t.accent : 'rgba(255,255,255,0.5)',
                                cursor: 'pointer',
                                padding: 0,
                                transition: 'all 0.2s',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      height: '210px',
                      background: `linear-gradient(135deg, ${t.accent}12 0%, ${t.accentPurple}12 100%)`,
                      border: `1px dashed ${t.border}`,
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexDirection: 'column', gap: '8px',
                    }}>
                      <div style={{ fontSize: '36px' }}>💻</div>
                      <div style={{ fontSize: '12px', color: t.textSub }}>Screenshot coming soon</div>
                    </div>
                  )}

                  {/* ── Content ── */}
                  <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', flex: 1 }}>

                    {/* Company label */}
                    {project.company && (
                      <div style={{ fontSize: '11px', color: t.accent, fontWeight: 600, marginBottom: '6px', letterSpacing: '0.5px' }}>
                        @ {project.company}
                      </div>
                    )}

                    {/* Title */}
                    <div style={{ fontSize: '15px', fontWeight: 700, color: t.text, marginBottom: '10px', lineHeight: 1.4 }}>
                      {project.title}
                    </div>

                    {/* Description */}
                    <div style={{ fontSize: '13px', color: t.textSub, lineHeight: 1.75, flex: 1, marginBottom: '14px' }}>
                      {project.description}
                    </div>

                    {/* Tech tags */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                      {project.tags.map(tag => (
                        <span key={tag} style={{
                          background: t.tagBg,
                          border: `1px solid ${t.tagBorder}`,
                          color: t.tagColor,
                          fontSize: '11px', fontWeight: 500,
                          padding: '3px 10px', borderRadius: '20px',
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Links */}
                    <div style={{
                      paddingTop: '14px',
                      borderTop: `1px solid ${t.border}`,
                      display: 'flex', alignItems: 'center', gap: '16px',
                    }}>
                      {project.github ? (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            fontSize: '13px', fontWeight: 600, color: t.text, textDecoration: 'none',
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = t.accent}
                          onMouseLeave={e => e.currentTarget.style.color = t.text}
                        >
                          <FaGithub size={15} /> GitHub
                        </a>
                      ) : (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          fontSize: '12px', color: t.textSub, fontStyle: 'italic',
                        }}>
                          🔒 Private / Proprietary
                        </span>
                      )}
                      {project.demo && (
                        <a
                          href={project.demo}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            fontSize: '13px', fontWeight: 600, color: t.accent, textDecoration: 'none',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                          <BsBoxArrowUpRight size={13} /> Live Demo
                        </a>
                      )}
                    </div>

                  </div>
                </div>
              </Col>
            );
          })}
        </Row>

      </Container>
    </section>
  );
};

export default Projects;